// Iter68 — Franchisees admin converted to card grid.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, FileCheck, BarChart3, Package } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { ProfileCard, ProfileCardGrid } from "@/components/admin/ProfileCard";

const STATUS_TONE = {
  pending:   { label: "Pending",   tone: "amber" },
  approved:  { label: "Approved",  tone: "green" },
  suspended: { label: "Suspended", tone: "rose" },
  rejected:  { label: "Rejected",  tone: "rose" },
};

export default function AdminFranchisesHome() {
  const [profiles, setProfiles] = useState([]);
  const [docs, setDocs] = useState([]);
  const [err, setErr] = useState("");

  const reload = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/admin/franchises/profiles"),
        api.get("/admin/franchises/documents?status=pending"),
      ]);
      setProfiles(a.data); setDocs(b.data);
    } catch (e) { setErr(apiErrorMessage(e)); }
  };
  useEffect(() => { reload(); }, []);

  const setCompliance = async (id, status) => {
    await api.put(`/admin/franchises/profiles/${id}/compliance`, { status });
    await reload();
  };
  const review = async (id, status) => {
    await api.put(`/admin/franchises/documents/${id}/review`, { status });
    await reload();
  };

  if (err) return <div className="text-rose-300">{err}</div>;

  return (
    <div data-testid="admin-franchises-home" className="space-y-6">
      <header className="fade-up">
        <div className="eyebrow">Franchise admin</div>
        <h1 className="headline-xl">Franchises</h1>
        <p className="text-lifted-dim mt-2 max-w-2xl text-sm">
          Compliance, attached partners, and territory metrics.
        </p>
      </header>

      <ProfileCardGrid testId="admin-franchises-profiles">
        {profiles.map((p) => {
          const status = p.compliance?.status || "pending";
          const tone = STATUS_TONE[status] || { label: status, tone: "amber" };
          return (
            <ProfileCard
              key={p.id}
              testId={`admin-franchise-${p.id}`}
              image={p.logo_url}
              topRightBadge={tone.label}
              title={p.territory_name || p.business_name || p.slug}
              subtitle={p.territory || p.business_name || "Franchisee"}
              meta={p.slug}
              chips={[
                { label: tone.label, tone: tone.tone },
              ]}
              stats={[
                { label: "Partners", value: p.stats?.partners_count ?? 0 },
                { label: "Revenue", value: `$${((p.stats?.total_revenue_cents || 0) / 100).toFixed(0)}` },
                { label: "Status",  value: status.slice(0, 4).toUpperCase() },
              ]}
              footer={
                <div className="inline-flex gap-2 w-full">
                  {status !== "approved" && (
                    <button
                      data-testid={`admin-franchise-approve-${p.id}`}
                      onClick={() => setCompliance(p.id, "approved")}
                      className="btn-lifted !py-1.5 !px-3 !text-[0.65rem] flex-1"
                    >Approve</button>
                  )}
                  {status === "approved" && (
                    <button
                      data-testid={`admin-franchise-suspend-${p.id}`}
                      onClick={() => setCompliance(p.id, "suspended")}
                      className="btn-ghost !py-1.5 !px-3 !text-[0.65rem] flex-1 hover:!text-rose-300"
                    >Suspend</button>
                  )}
                </div>
              }
            />
          );
        })}
        {profiles.length === 0 && (
          <div className="col-span-full glass-panel p-8 text-center text-lifted-mute">
            No franchises yet. Approve a franchise application from /admin/applications.
          </div>
        )}
      </ProfileCardGrid>

      <section className="glass-panel p-5" data-testid="admin-franchises-doc-review">
        <div className="eyebrow !text-lifted-text-dim flex items-center gap-2 mb-3"><FileCheck className="h-3 w-3" /> Pending document reviews</div>
        <ul className="space-y-2 text-sm">
          {docs.map((d) => (
            <li key={d.id} data-testid={`admin-franchise-doc-${d.id}`} className="flex items-center justify-between border-b border-white/5 pb-2 last:border-b-0">
              <div>
                <div className="text-lifted-text">{d.label} <span className="chip">{d.kind}</span></div>
                <div className="text-xs text-lifted-dim break-all">{d.url}</div>
              </div>
              <div className="flex gap-2">
                <button data-testid={`admin-franchise-doc-approve-${d.id}`} onClick={() => review(d.id, "approved")} className="btn-lifted !py-1.5 !px-3 !text-[0.65rem]">Approve</button>
                <button data-testid={`admin-franchise-doc-reject-${d.id}`} onClick={() => review(d.id, "rejected")} className="btn-ghost !py-1.5 !px-3 !text-[0.65rem] hover:!text-rose-300">Reject</button>
              </div>
            </li>
          ))}
          {docs.length === 0 && <li className="text-lifted-mute text-xs">No pending document reviews.</li>}
        </ul>
      </section>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Link to="/admin/delivery/dispatch" className="glass-panel p-5 hover:-translate-y-1 transition-transform" data-testid="admin-franchises-dispatch-link">
          <div className="flex items-center gap-3">
            <Package className="h-5 w-5 text-lifted-green" />
            <div>
              <div className="font-anton text-lg uppercase">Dispatch board</div>
              <div className="text-xs text-lifted-dim">Single board for all orders.</div>
            </div>
          </div>
        </Link>
        <Link to="/admin/franchises/metrics" className="glass-panel p-5 hover:-translate-y-1 transition-transform" data-testid="admin-franchises-metrics-link">
          <div className="flex items-center gap-3">
            <BarChart3 className="h-5 w-5 text-lifted-green" />
            <div>
              <div className="font-anton text-lg uppercase">Metrics</div>
              <div className="text-xs text-lifted-dim">Partner count, completed orders, revenue per franchise.</div>
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
}
